/**
 * ENG-9840 — letting a channel credential write say WHY it did not land.
 *
 * THE BUG THIS EXISTS TO FIX. `writeChannelCredentials` returns `void` and has
 * nine bare-`return` exit points: four missing-credential-field guards (one per
 * channel), three `writeMcpJsonGuarded` rejections, and two success paths. None
 * of them throw, so all nine are indistinguishable to the caller.
 *
 * ENG-8290 added the honest half of the check — the manager verifies the
 * credential is actually on disk instead of reading "did not throw" as "wrote".
 * That correctly detects the failure. It then has to GUESS at the cause, which
 * is why the operator-facing line reads:
 *
 *     credentials for telegram reported a clean write but did not land on disk
 *     (reason=first-write) — likely a missing credential field, or the guarded
 *     .mcp.json write was rejected by the secret lint / config validator
 *
 * Two separate defects in one sentence. "likely A, or B" is a guess offered
 * where a cause belongs, and `reason=first-write` is the write TRIGGER, not a
 * cause, presented as though it were one. Every remedy a responder can take
 * from that line is a coin flip.
 *
 * The cost is not theoretical and it is not small. At the time of writing, four
 * open `channel_sync_stuck` alerts carry this message and between them have
 * burned 9,277 write attempts that could never succeed: mustard 3,551 over 13.8
 * days, sterling 2,778 over 10.1 days, sheraz (High risk tier, customer org)
 * 1,979 over 7.3 days. sterling's has been retrying every 300s since 40 minutes
 * after the agent was created and has never once converged. On the same tick
 * that logs the failure the manager logs `decision=healthy`, so every health
 * surface reads green while the channel is unusable.
 *
 * The retry is not eternal because retrying is wrong — ENG-9739 established
 * that terminating on a guessed condition is worse, having taken six agents
 * down doing exactly that. It is eternal because NOTHING CAN EVER LEARN WHY IT
 * FAILS. This module is the missing return channel, and nothing more: it adds
 * no terminal state, no give-up bound, and changes no retry behaviour.
 *
 * The rule this module encodes, inherited from `kanban-write-outcome.ts`:
 * an outcome this build does not recognise FAILS CLOSED to "unknown". An
 * adapter that has not opted in returns `undefined`, and `undefined` must never
 * be read as success — reading an absent answer as a good one is the same
 * confidently-wrong move the whole ticket is about.
 */

/**
 * Why a channel credential write did not land, or that it did.
 *
 * Deliberately a small closed union rather than one code per `return`. Nine
 * exit points collapse to two causes because two causes are what a responder
 * can actually act on differently: fix the config, or go read why the guard
 * rejected the file. Splitting `missing_credential_field` per channel would
 * make the type track the adapter's branch structure instead of the operator's
 * decision, and `field` already carries that detail.
 */
export type ChannelWriteOutcome =
  | { ok: true }
  | {
      ok: false;
      reason: 'missing_credential_field';
      /** The config key that was absent, e.g. `bot_token`. Named, not guessed. */
      field: string;
    }
  | { ok: false; reason: 'mcp_json_rejected' };

/** The write landed. */
export const CHANNEL_WRITE_OK: ChannelWriteOutcome = { ok: true };

/** The channel's config is missing a required credential field, so nothing was written. */
export function missingCredentialField(field: string): ChannelWriteOutcome {
  return { ok: false, reason: 'missing_credential_field', field };
}

/** `writeMcpJsonGuarded` refused the rendered config (secret lint / validator). */
export const CHANNEL_WRITE_MCP_JSON_REJECTED: ChannelWriteOutcome = {
  ok: false,
  reason: 'mcp_json_rejected',
};

/**
 * The cause clause for the operator-facing message.
 *
 * Returns a statement of fact with no hedging in it. The one branch that is
 * allowed to be uncertain is the one where we genuinely are: an adapter that
 * predates ENG-9840 returns `undefined`, and there the honest answer is that
 * the write did not land and this build cannot say why — which is still better
 * than the old line, because it does not dress a guess up as a diagnosis.
 *
 * `trigger` is the write trigger (`first-write` / `on-disk-missing` /
 * `content-changed`). It is passed in and labelled as a trigger so it stops
 * being read as a cause.
 */
export function describeChannelWriteFailure(
  outcome: ChannelWriteOutcome | undefined | void,
  channelId: string,
  trigger: string,
): string {
  const prefix = `credentials for ${channelId} did not land on disk (trigger=${trigger})`;

  // FAIL CLOSED. `undefined` means an adapter that has not opted in, NOT a
  // successful write. See the module header.
  if (!outcome) {
    return (
      `${prefix} and this adapter does not report a cause. ` +
      `Check the channel config for a missing credential field, then the ` +
      `.mcp.json guard rejection printed once per fingerprint on stderr.`
    );
  }

  if (outcome.ok) {
    // The writer claimed success and the on-disk check disagreed. That is a
    // genuinely different bug from either failure branch and must not be
    // reported as one of them.
    return (
      `${prefix} even though the writer reported it wrote. ` +
      `The write path and the on-disk check disagree — this is not a config problem.`
    );
  }

  switch (outcome.reason) {
    case 'missing_credential_field':
      return (
        `${prefix}: cause=missing_credential_field(${outcome.field}). ` +
        `The ${channelId} channel config has no ${outcome.field}, so the writer ` +
        `returned without writing. Set it on the channel and the next sync converges.`
      );
    case 'mcp_json_rejected':
      return (
        `${prefix}: cause=mcp_json_rejected. The rendered .mcp.json was refused by ` +
        `the secret lint / config validator, so nothing was written. The specific ` +
        `rejection is printed once per fingerprint on stderr.`
      );
    default: {
      // A newer adapter reporting a reason this build has never heard of. Say
      // it verbatim rather than falling back to a familiar-sounding cause — an
      // unknown word a responder can search for beats a confident wrong answer.
      const unknown = outcome as { reason?: string };
      return `${prefix}: cause=${unknown.reason ?? 'unrecognised'} (unknown to this build).`;
    }
  }
}
